'use client'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

interface AppDownloadBannerProps {
  name: string
  url: string
  icon: string
  androidPackage: string
}

// Só Chrome/Android; lê `related_applications` de src/app/manifest.ts.
type NavigatorWithRelatedApps = Navigator & {
  getInstalledRelatedApps?: () => Promise<{ id?: string; platform: string }[]>
}

const DISMISS_KEY = 'app-open-prompt-dismissed'
const PROMPT_TRANSITION_MS = 300

function wasDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

/** Faixa fixa no rodapé estilo "smart app banner": se o app está instalado, oferece abrir nele (com prompt); senão, leva à loja. */
export function AppDownloadBanner({
  name,
  url,
  icon,
  androidPackage,
}: AppDownloadBannerProps) {
  const pathname = usePathname()
  const [href, setHref] = useState(url)
  // `null` = detectando; até lá usa o intent, que já cai na loja se não houver app.
  const [installed, setInstalled] = useState<boolean | null>(null)
  const [promptOpen, setPromptOpen] = useState(false)
  const [promptVisible, setPromptVisible] = useState(false)
  const openButtonRef = useRef<HTMLAnchorElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: recalcula a cada navegação
  useEffect(() => {
    const { host, pathname: path, search } = window.location
    setHref(
      `intent://${host}${path}${search}#Intent;scheme=https;package=${androidPackage};S.browser_fallback_url=${encodeURIComponent(url)};end`
    )
  }, [pathname, androidPackage, url])

  useEffect(() => {
    const nav = navigator as NavigatorWithRelatedApps
    Promise.resolve()
      .then(() => nav.getInstalledRelatedApps?.())
      .then(apps => {
        const found = !!apps?.some(app => app.id === androidPackage)
        setInstalled(found)
        if (found && !wasDismissed()) {
          setPromptOpen(true)
        }
      })
      .catch(() => setInstalled(false))
  }, [androidPackage])

  const dismissPrompt = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // storage bloqueado: o prompt volta no próximo carregamento
    }
    setPromptVisible(false)
    setTimeout(() => setPromptOpen(false), PROMPT_TRANSITION_MS)
  }, [])

  // Monta fora da tela e anima no próximo frame para a transição CSS rodar.
  useEffect(() => {
    if (!promptOpen) {
      return
    }
    const frame = requestAnimationFrame(() => setPromptVisible(true))
    openButtonRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismissPrompt()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [promptOpen, dismissPrompt])

  const showStore = installed === false

  return (
    <>
      <div aria-hidden className="h-[calc(60px+env(safe-area-inset-bottom))]" />
      <div className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-3 border-neutral-200 border-t bg-white px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        <Image
          alt=""
          className="rounded-xl"
          height={44}
          src={icon}
          width={44}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-neutral-900 text-sm">
            {name}
          </p>
          <p className="truncate text-neutral-500 text-xs">
            {showStore ? 'Baixe o aplicativo' : 'Abra no aplicativo'}
          </p>
        </div>
        {showStore ? (
          <a
            className="cursor-pointer rounded-full bg-primary px-4 py-1.5 font-semibold text-primary-foreground text-sm"
            href={url}
            rel="noopener noreferrer"
            target="_blank"
          >
            Baixar
          </a>
        ) : (
          <a
            className="cursor-pointer rounded-full bg-primary px-4 py-1.5 font-semibold text-primary-foreground text-sm"
            href={href}
          >
            Abrir no app
          </a>
        )}
      </div>

      {promptOpen && (
        <div className="fixed inset-0 z-[60] flex items-end">
          <button
            aria-label="Continuar no navegador"
            className={`absolute inset-0 cursor-default bg-black/40 transition-opacity duration-300 ease-out ${promptVisible ? 'opacity-100' : 'opacity-0'}`}
            onClick={dismissPrompt}
            tabIndex={-1}
            type="button"
          />
          <div
            aria-labelledby="app-open-prompt-title"
            aria-modal="true"
            className={`relative w-full rounded-t-2xl bg-white px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-[0_-8px_24px_rgba(0,0,0,0.12)] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${promptVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}
            role="dialog"
          >
            <div className="flex flex-col items-center gap-4 text-center">
              <Image
                alt=""
                className="rounded-2xl"
                height={64}
                src={icon}
                width={64}
              />
              <h2
                className="font-semibold text-lg text-neutral-900"
                id="app-open-prompt-title"
              >
                Abrir no {name}?
              </h2>
              <a
                className="w-full cursor-pointer rounded-full bg-primary px-4 py-3 font-semibold text-primary-foreground"
                href={href}
                ref={openButtonRef}
              >
                Abrir no app
              </a>
              <button
                className="w-full cursor-pointer rounded-full px-4 py-3 font-semibold text-neutral-600"
                onClick={dismissPrompt}
                type="button"
              >
                Continuar no navegador
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
